/**
 * Drag-rectangle window / crossing select for the Three.js renderer.
 *
 * AutoCAD parity:
 *   * **window select** — entity must be **fully inside** the rectangle
 *     (every vertex inside; every segment endpoint inside). Drawn left-
 *     to-right in AutoCAD with a solid blue rectangle.
 *   * **crossing select** — entity selected if **any vertex inside** OR
 *     **any segment intersects** the rectangle border. Drawn right-to-
 *     left in AutoCAD with a dashed green rectangle.
 *
 * The math is **ported, not imported** from
 * `apps/web/src/app/scene/entityBounds.ts:isWithinSelectionWindow` and
 * `intersectsSelectionWindow`. We can't import the SVG version because:
 *
 *   1. Architecture rule (CLAUDE.md `scene3d/` is a parallel
 *      pipeline; reaching into `scene/` couples the two).
 *   2. The SVG version reads from `EntityPreview.properties` (string
 *      map). The WebGL version reads from `RenderableEntity.geometry`
 *      (typed). Different shapes — same algorithm.
 *
 * The port keeps the original AutoCAD-parity edge semantics.
 */

import { isPointInFilledRegion } from "@dxf-ai-editor/drawing-model";

import {
  ellipseMinorUnit,
  tessellateEllipse,
} from "../adapters/arcEllipseSpline";
import { polylineCentrelineSamples } from "../adapters/geom/polylineWidthBand";
import {
  drawnBandFilledRegion,
  fillModePaintsSolid,
} from "../adapters/geom/polylineDrawnBand";
import { drawnPolylineSweptRuns } from "../adapters/geom/polylineThicknessSweep";
import { pointInPaintedHatch } from "./hatchFilledRegion";
import type { ChildGeometryFilter } from "../layerChainVisibility";
import { isMetadataStubRenderable } from "../state/metadataStub";
import type {
  RenderableEntity,
  SceneGeometry,
  SpatialIndex,
  Vec2,
  Vec3,
} from "../types";

export type SelectionMode = "window" | "crossing";

export interface SelectionWindow {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * MC4 S2 P2 (#C) — optional refinement that runs the analytic window/crossing
 * containment in a DIFFERENT space than the AABB prefilter.
 *
 * For a twisted VP the paper rubber-band maps to a ROTATED parallelogram in
 * model space; the AABB query (`prefilterWindow`) is only a loose enclosing box
 * (it MUST stay loose so the index prefilter never drops a real hit). The
 * precise containment must run against the original axis-aligned PAPER rectangle
 * (`testWindow`), with every candidate point mapped back to paper via `mapPoint`
 * (= `space.fromSpace`, modelToPaper). Identity (no twist / no redirect) → caller
 * omits this so the model AABB IS the test window (byte-equivalent to the legacy
 * 1-window path).
 */
export interface WindowRefinement {
  /** Axis-aligned rectangle the analytic predicate tests against (paper space). */
  readonly testWindow: SelectionWindow;
  /** Map a candidate geometry point into the test-window space (model → paper). */
  readonly mapPoint: (p: Vec3) => Vec2;
}

/**
 * Run a window/crossing select against the supplied spatial index. The
 * spatial index pre-filters by AABB → analytic check refines per-mode.
 *
 * Opt 3 — the index returns ids; we resolve through its entity resolver
 * before handing candidates to the per-mode predicate. Ids whose
 * resolver returns `undefined` (stale id between intersect and resolve)
 * are skipped silently — equivalent to "entity no longer rendered".
 *
 * MC4 S2 P2 (#C) — when `refine` is supplied the AABB `window` is used ONLY as
 * the index prefilter; the per-mode analytic containment runs against
 * `refine.testWindow` with each candidate point mapped through `refine.mapPoint`
 * (the rotated-parallelogram → paper-rectangle test for twisted VPs). Without it
 * the analytic test runs against `window` directly (legacy behaviour).
 */
export function selectInWindow(
  window: SelectionWindow,
  mode: SelectionMode,
  spatialIndex: SpatialIndex,
  refine?: WindowRefinement,
  /**
   * Codex PR #784 round 3 P2 — "is this child currently DRAWN" predicate for a
   * `block-insert` wrapper's `childRenderables`
   * (`layerChainVisibility.ts makeVisibleChildGeometryFilter`). Omitted →
   * every child participates (nothing hidden).
   */
  childFilter?: ChildGeometryFilter | null,
): string[] {
  const candidateIds = spatialIndex.intersect({
    left: window.left,
    top: window.top,
    right: window.right,
    bottom: window.bottom,
  });
  const candidates: RenderableEntity[] = [];
  for (const id of candidateIds) {
    const entity = spatialIndex.resolve(id);
    if (entity) candidates.push(entity);
  }
  return selectFromCandidates(window, mode, candidates, refine, childFilter);
}

/**
 * Run a window/crossing select against an explicit candidate list.
 * Used by tests and by callers that already have a candidate slice.
 *
 * `refine` (MC4 S2 P2 #C) — see {@link WindowRefinement}. When present the
 * analytic predicate tests `refine.testWindow` using points mapped through
 * `refine.mapPoint`; otherwise it tests `window` in the candidates' native space.
 */
export function selectFromCandidates(
  window: SelectionWindow,
  mode: SelectionMode,
  candidates: ReadonlyArray<RenderableEntity>,
  refine?: WindowRefinement,
  /** See {@link selectInWindow}'s `childFilter`. */
  childFilter?: ChildGeometryFilter | null,
): string[] {
  const testWindow = refine ? refine.testWindow : window;
  const mapPoint = refine ? refine.mapPoint : null;
  const filter = childFilter ?? null;
  const out: string[] = [];
  for (const entity of candidates) {
    if (entityMatchesWindow(entity, mode, testWindow, mapPoint, filter)) {
      out.push(entity.id);
    }
  }
  return out;
}

/**
 * One DRAWN RUN of a multi-run entity as a standalone `polyline` renderable, so
 * the existing per-mode predicates test it with the polyline rule.
 *
 * Three producers share it, because they share the whole argument: a `mesh-wire`
 * entity's rows / columns / face-edge loops, a wide polyline's BAND silhouette
 * rings, and the base / swept-top / RULING runs of a group-39 thickness
 * (`adapters/geom/polylineThicknessSweep.ts::drawnPolylineSweptRuns`). Each is a
 * DISJOINT stroke of ONE entity, so testing the flattened point list instead
 * would build phantom segments between the end of one run and the start of the
 * next — strokes that exist nowhere in the drawing.
 *
 * The run keeps the PARENT's id: these are render-only and never exposed as
 * selection results — the wrapper is the single selectable entity (the same
 * contract `block-insert`'s children have). The geometry is REPLACED rather than
 * spread, so the child carries neither `widths` nor `thicknessSweep` and the
 * recursion in {@link entityMatchesWindow} terminates on it
 * ({@link drawnPolylineSweptRuns} answers `null` for a bare run).
 *
 * The point list is COPIED because `SceneGeometry.polyline.points` is mutable
 * while a drawn run is `readonly`. The copy is free in order terms — the bbox
 * fold below already walks every one of these points — and it is the honest
 * direction: a throw-away child must not hand a caller a writable alias of the
 * band silhouette the batcher is about to stroke.
 */
function drawnRunRenderable(
  parent: RenderableEntity,
  points: readonly Vec3[],
  closed: boolean,
): RenderableEntity {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    ...parent,
    geometry: { kind: "polyline", points: [...points], closed },
    bounds: { minX, minY, maxX, maxY },
  };
}

/**
 * CROSSING select against a HATCH's PAINTED INTERIOR — @codex PR #785 round 17,
 * P2 (the hatch half).
 *
 * # The defect, in both directions
 *
 * A hatch reaches the generic predicate as its boundary loops
 * ({@link collectGeometryPoints} returns the exterior ring, {@link
 * buildTestSegments} closes it), so a crossing rectangle hits only when a ring
 * VERTEX lands in the rubber band or a ring EDGE crosses its border. Measured on
 * a 100x100 SOLID hatch, a 20x20 crossing rectangle at its CENTRE returned `[]`
 * in EVERY `$FILLMODE` state — while the pointer pick answered 0 at that same
 * centre and the canvas painted a fill mesh over it. That is round 12's F1 all
 * over again, one entity read as two different shapes by two selection paths,
 * with the wide-polyline band fixed ({@link crossingReachesBandFill}) and the
 * hatch left behind.
 *
 * At an explicit `$FILLMODE` 0 the answer `[]` was already RIGHT, for the wrong
 * reason: nothing is painted inside, so edge-only is the correct behaviour
 * there. {@link pointInPaintedHatch} states both halves at once — it is the SAME
 * function the pointer pick's hatch arm calls, and it returns `false`
 * EVERYWHERE at `$FILLMODE` 0, so this test contributes nothing in the zero
 * state and the outlined hatch stays selectable by its stroked boundary alone.
 *
 * # The rule
 *
 * A rectangle with no crossed edge is either entirely inside the painted region
 * or entirely outside it, so ONE containment test per window corner decides it
 * ({@link windowCorners}) — the same shape {@link crossingReachesBandFill} uses.
 * HOLES are subtracted (exterior MINUS holes, never "inside the outer loop"), so
 * a rectangle in an empty island hole keeps missing exactly as the pointer
 * reports a non-zero distance there.
 *
 * The mapped (twisted-viewport) path projects the hatch loops into the test
 * window's PAPER space through the same `mapPoint` every other candidate point
 * goes through, so the corners and the region are compared in one space.
 */
function crossingReachesHatchFill(
  entity: RenderableEntity,
  testWindow: SelectionWindow,
  mapPoint: ((p: Vec3) => Vec2) | null,
): boolean {
  for (const corner of windowCorners(testWindow)) {
    if (pointInPaintedHatch(corner, entity, mapPoint)) return true;
  }
  return false;
}

/**
 * CROSSING select against the band's PAINTED INTERIOR — @codex PR #785 round 12,
 * F1 (P2).
 *
 * # The defect
 *
 * The loop recursion above tests each silhouette ring as a closed polyline:
 * a hit needs a ring VERTEX inside the rubber band or a ring EDGE crossing its
 * border. Round 11 (`69d174a`) made the interior those rings enclose REAL
 * VISIBLE GEOMETRY — at the Autodesk-documented default `$FILLMODE` 1 the
 * canvas emits a triangulated fill mesh (`scene/widePolylineFillMesh.ts`) and
 * the pointer pick answers 0 anywhere inside it
 * (`raycast/distance.ts::distanceToDrawnBand`) — so a right-to-left rectangle
 * lying WHOLLY INSIDE a sufficiently wide ribbon satisfies neither condition
 * and missed, while the pointer, the spatial index (round 6/10) and the plot
 * all agreed the entity occupies that space. One entity, several consumers,
 * one of them reading a different shape: the same defect this PR has closed
 * round after round.
 *
 * # The rule
 *
 * A rectangle with no crossed edge is either entirely inside the painted region
 * or entirely outside it, so ONE containment test per window corner decides it
 * ({@link windowCorners} — the same inverse-containment shape the mapped
 * bbox-corner path in `crossesWindow` already uses for a surrounding block).
 *
 * HOLES: the test is EXTERIOR MINUS HOLES, never "inside the outer loop". A
 * CLOSED band is an ANNULUS whose middle is empty on screen, so a rectangle in
 * the middle of a 10x10 width-2 ring must keep missing — exactly as the pointer
 * reports 4.0 there and never 0. That is `drawing-model`'s one-truth
 * `isPointInFilledRegion` over {@link drawnBandFilledRegion}, the SAME region
 * the pick builds; there is deliberately no second point-in-band test.
 *
 * `$FILLMODE` 0: nothing is painted inside, the canvas draws the silhouette
 * alone, and the behaviour stays EDGE-ONLY — this returns `false` without
 * building a region at all.
 *
 * The mapped (twisted-viewport) path maps the band's loops into the test
 * window's PAPER space through the same `mapPoint` every other candidate point
 * goes through, so the corners and the region are compared in one space.
 */
function crossingReachesBandFill(
  entity: RenderableEntity,
  geom: Extract<SceneGeometry, { kind: "polyline" }>,
  loops: Vec3[][],
  testWindow: SelectionWindow,
  mapPoint: ((p: Vec3) => Vec2) | null,
): boolean {
  if (!fillModePaintsSolid(entity.fillmode)) return false;
  const region = drawnBandFilledRegion(loops, geom, mapPoint);
  for (const corner of windowCorners(testWindow)) {
    if (isPointInFilledRegion(corner, region)) return true;
  }
  return false;
}

/**
 * Per-mode predicate for ONE candidate.
 *
 * Codex PR #784 P2 — a cache-direct INSERT wrapper (`block-insert` with
 * `childRenderables`, `scene/liftBlockInsert.ts`) is the only spatial-index
 * entry for the reference, so the test must run on its CHILDREN — the drawn
 * strokes — not on the wrapper's AABB (a crossing band through the empty
 * interior of a sparse block must NOT select it). The rule follows AutoCAD,
 * which tests the block's geometry: WINDOW selects the reference only when
 * EVERY child is fully inside; CROSSING when ANY child crosses. The result is
 * always the WRAPPER's id — child ids are never exposed (the legacy flat-push
 * branch, where children are their own index entries, selects child ids
 * instead). Recurses through the same predicate so a nested wrapper child or
 * any per-kind child uses its own rule. A child with non-finite bounds is
 * skipped (mirrors `liftBlockInsert.ts`'s union guard — it contributes no
 * geometry, so it must neither veto a WINDOW nor fake a CROSSING hit); a child
 * `childFilter` rejects (currently NOT drawn — round 3 P2) is skipped for the
 * same reason; the legacy InstancedMesh stub (no children) and a wrapper with
 * no finite / no visible child keep the bounds test.
 */
function entityMatchesWindow(
  entity: RenderableEntity,
  mode: SelectionMode,
  testWindow: SelectionWindow,
  mapPoint: ((p: Vec3) => Vec2) | null,
  childFilter: ChildGeometryFilter | null,
): boolean {
  const geom = entity.geometry;
  if (geom.kind === "mesh-wire") {
    // A heavy POLYLINE mesh is tested against the wire runs it DRAWS, exactly
    // as a cache-direct INSERT is tested against its children below: WINDOW
    // selects only when EVERY run is fully inside, CROSSING when ANY run
    // crosses. Testing the flattened point list instead would build phantom
    // segments between the END of one row and the START of the next — strokes
    // that exist nowhere in the drawing — so a crossing band through the empty
    // middle of a coarse mesh would select it (@codex PR #785 round 3, F4).
    let tested = 0;
    for (const run of geom.runs) {
      const child = drawnRunRenderable(entity, run.points, run.closed);
      const b = child.bounds;
      if (!Number.isFinite(b.minX + b.minY + b.maxX + b.maxY)) continue;
      tested++;
      const hit = entityMatchesWindow(
        child,
        mode,
        testWindow,
        mapPoint,
        childFilter,
      );
      if (mode === "window") {
        if (!hit) return false;
      } else if (hit) {
        return true;
      }
    }
    if (tested > 0) return mode === "window";
  }
  if (geom.kind === "polyline") {
    // A polyline carrying authored WIDTHS is drawn as its BAND SILHOUETTE, not
    // as a centreline (`batchedLayer.ts::widePolylineLineVertices`), and one
    // carrying a group-39 THICKNESS is drawn as the SWEPT surface that shape
    // generates — its base, the copy translated by `t·N̂`, and the rulings
    // between them (`adapters/geom/polylineThicknessSweep.ts`). Every one of
    // those is as disjoint as a mesh's wire runs, so they are tested with the
    // SAME per-run rule one block up: WINDOW selects only when EVERY run is
    // fully inside, CROSSING when ANY run crosses.
    //
    // Testing the centreline instead missed a right-to-left crossing rectangle
    // that touches only the visible band EDGE — which the render AABB
    // (`adapters/geom/lwpolylineGeometry.ts::expandBoundsForWidths` pads it by
    // the largest half-width) already admits as a CANDIDATE, so the entity
    // reached this predicate and was then rejected against a curve nobody draws
    // (@codex PR #785 round 7). ROUND 16 IS THE SAME SENTENCE FOR THE SWEEP:
    // round 15 painted the top outline and this predicate went on testing the
    // base, so a crossing rectangle laid exactly over a PAINTED top edge
    // selected nothing — extrusion `(1,1,1)`, thickness 5, width 4 on
    // `(0,0) → (10,0)` paints a top vertex at `(3.7032479268758554,
    // 3.7032479268758554)` and a 0.16-wide crossing box centred on it returned
    // `[]`, while the entity's own bounds (`maxX` 3.7032479268758554) had
    // admitted it as a candidate the whole time.
    //
    // `drawnPolylineSweptRuns` is the SAME assembly the batcher paints from
    // (pinned segment-for-segment by
    // `raycast/thicknessSweptPick.property.test.ts`),
    // and it answers `null` for the width-less, thickness-less majority, which
    // then falls through to the byte-identical centreline path below.
    const drawn = drawnPolylineSweptRuns(geom);
    if (drawn) {
      let tested = 0;
      for (const run of drawn.runs) {
        const child = drawnRunRenderable(entity, run.points, run.closed);
        const b = child.bounds;
        if (!Number.isFinite(b.minX + b.minY + b.maxX + b.maxY)) continue;
        tested++;
        const hit = entityMatchesWindow(
          child,
          mode,
          testWindow,
          mapPoint,
          childFilter,
        );
        if (mode === "window") {
          if (!hit) return false;
        } else if (hit) {
          return true;
        }
      }
      if (tested > 0) {
        // WINDOW: every run was fully inside, so the region they bound is too.
        if (mode === "window") return true;
        // CROSSING: no drawn EDGE was crossed and no drawn VERTEX landed in the
        // rubber band — but at `$FILLMODE` 1 the BASE ribbon's INTERIOR is
        // painted, so a rectangle lying wholly inside it is lying on the entity.
        // The BASE loops only: the swept top is stroked as an outline and never
        // filled (`scene/widePolylineFillMesh.ts`), so widening the fill test to
        // it would select through a ribbon the canvas leaves hollow. See
        // `crossingReachesBandFill`.
        return drawn.bandLoops
          ? crossingReachesBandFill(
              entity,
              geom,
              drawn.bandLoops,
              testWindow,
              mapPoint,
            )
          : false;
      }
    }
  }
  if (geom.kind === "block-insert") {
    let tested = 0;
    let hiddenChildren = 0;
    for (const child of geom.childRenderables) {
      // Codex PR #784 round 3 P2 — a child hidden by its OWN layer chain
      // (global off / freeze, or frozen in the ACTIVE viewport) draws nothing,
      // so it must neither let a CROSSING band through empty space select the
      // INSERT nor VETO a WINDOW selection by lying outside the rubber band.
      // Skipping it entirely (not counting it in `tested`) is both halves.
      if (childFilter !== null && !childFilter(child)) {
        hiddenChildren++;
        continue;
      }
      const b = child.bounds;
      // A finite sum ⇔ every corner finite (NaN / ±Infinity propagate).
      if (!Number.isFinite(b.minX + b.minY + b.maxX + b.maxY)) continue;
      tested++;
      const hit = entityMatchesWindow(
        child,
        mode,
        testWindow,
        mapPoint,
        childFilter,
      );
      if (mode === "window") {
        if (!hit) return false; // WINDOW: one child outside vetoes.
      } else if (hit) {
        return true; // CROSSING: one crossing child suffices.
      }
    }
    // Every finite child passed (WINDOW) / none crossed (CROSSING).
    if (tested > 0) return mode === "window";
    // Nothing testable was left AND at least one child was dropped as hidden:
    // the reference draws no geometry here, so neither mode may select it. (A
    // wrapper whose children are all NON-finite, or the legacy InstancedMesh
    // stub with no children at all, still falls through to the bounds test.)
    if (hiddenChildren > 0) return false;
  }
  if (geom.kind === "hatch" && mode === "crossing") {
    // A CROSSING rectangle lying wholly inside the hatch crosses no boundary
    // ring and contains no ring vertex, yet at a filling `$FILLMODE` the canvas
    // paints a mesh over it and the pointer pick answers 0 there. See
    // {@link crossingReachesHatchFill}; at an explicit `$FILLMODE` 0 it is
    // `false` everywhere and the behaviour stays edge-only. WINDOW mode is
    // fully-inside semantics (every boundary vertex in the rubber band) and is
    // untouched — the boundary is stroked in BOTH fill states.
    return (
      crossesWindow(entity, testWindow, mapPoint) ||
      crossingReachesHatchFill(entity, testWindow, mapPoint)
    );
  }
  return mode === "window"
    ? isFullyInsideWindow(entity, testWindow, mapPoint)
    : crossesWindow(entity, testWindow, mapPoint);
}

/*
 * MC4 S2 P2 (#B, iter-4) — a SPATIAL STUB masquerading as a `point`.
 *
 * The predicate itself (`isMetadataStubRenderable`) lives beside its producer,
 * `state/metadataStub.ts`; this note records why WINDOW SELECT cares. In
 * cache-direct mode (huge drawings) the spatial index resolves metadata STUBS
 * for EVERY entity kind: `metadataToSpatialStub` (`state/spatialBuilder.ts`)
 * emits `geometry.kind === "point"` whose `position` is the bbox CENTRE, while
 * keeping the entity's ORIGINAL `type` (`"Line"`, `"Circle"`, `"MTEXT"`, …). A
 * genuine DXF POINT keeps `type === "Point"`. So `kind === "point"` with a type
 * OTHER than `"Point"` is a placeholder whose single `geom.position` does NOT
 * describe the real geometry — only the metadata `bounds` does. The click path
 * lifts these through the cache before refinement (`handleClickPick`); the
 * window-select path runs against the raw index records, so it must lift the
 * stub's geometry to its bbox corners itself.
 */

/** Half-edge below which a bbox dimension is treated as zero (float slack). */
const POINT_BOUNDS_EPSILON = 1e-9;

/**
 * MC4 S2 P2 (#C, iter-7) — a REAL DXF `Point` whose visible marker has a
 * NON-DEGENERATE bbox.
 *
 * `pointToRenderable` gives a `Point` a marker bbox from PDSIZE (or a default
 * ~1-unit half-size when PDSIZE = 0) — the square/cross glyph AutoCAD draws at
 * the insertion point. The entity's only GEOMETRY point is the insertion point
 * (`collectGeometryPoints` → `[geom.position]`, the bbox CENTRE), so in the
 * mapped/twisted path the single mapped centre would be the only tested point and
 * the visible marker bounds would be ignored — a left-to-right WINDOW select
 * could wrongly include a point whose marker protrudes OUTSIDE the rubber band as
 * long as the centre is inside. (`isMetadataStubRenderable` excludes a real
 * Point because it keeps `type === "Point"`, so the iter-4 stub lift never
 * covered this case.)
 *
 * A real Point with a non-degenerate marker bbox must therefore be tested by its
 * 4 mapped bbox corners — the SAME mapped-corner path used for text / block /
 * dimension / stub. A truly DEGENERATE point (zero-size bounds, e.g. PDSIZE 0
 * collapsed to a dot) keeps testing its single mapped insertion point.
 */
function isNonDegeneratePoint(entity: RenderableEntity): boolean {
  if (entity.geometry.kind !== "point" || entity.type !== "Point") return false;
  const b = entity.bounds;
  return (
    b.maxX - b.minX > POINT_BOUNDS_EPSILON ||
    b.maxY - b.minY > POINT_BOUNDS_EPSILON
  );
}

/** The entity's 4 bbox corners (CCW), mapped through `mapPoint` (model → paper). */
function mappedBboxCorners(
  entity: RenderableEntity,
  mapPoint: (p: Vec3) => Vec2,
): Vec2[] {
  const b = entity.bounds;
  return [
    mapPoint({ x: b.minX, y: b.minY, z: 0 }),
    mapPoint({ x: b.maxX, y: b.minY, z: 0 }),
    mapPoint({ x: b.maxX, y: b.maxY, z: 0 }),
    mapPoint({ x: b.minX, y: b.maxY, z: 0 }),
  ];
}

/**
 * MC4 S2 P2 (#C) — the candidate's representative points for the analytic test,
 * already mapped into the test-window space, plus whether those points are the
 * entity's 4 BBOX CORNERS (a closed ring) rather than its native vertex list.
 *
 * When `mapPoint` is null the entity's native geometry points are returned
 * unchanged (legacy path, `bboxCorners: false`). When set, each point is mapped
 * (model → paper). Two cases yield the 4 mapped bbox corners instead of vertex
 * points so the rotated frame is honoured rather than the axis-aligned min/max
 * shortcut (invalid post-rotation):
 *
 *   - a bbox-only entity (text / block / dimension / multileader — no geometry
 *     points at all);
 *   - a SPATIAL STUB (cache-direct `point` placeholder, iter-4 #2) whose single
 *     `geom.position` is the bbox centre and does NOT describe the real shape —
 *     mirroring the cache lift the click path performs (`handleClickPick`);
 *   - a REAL DXF `Point` (iter-7) whose single geometry point is the marker
 *     CENTRE but whose renderable `bounds` is the NON-DEGENERATE PDSIZE marker —
 *     testing the centre alone would ignore the visible glyph that protrudes past
 *     the rubber band (`isNonDegeneratePoint`).
 *
 * `bboxCorners: true` tells `buildTestSegments` to CLOSE the 4-corner ring
 * (iter-4 #3) so a crossing drag over the rotated bbox sees all four edges.
 */
function testPoints(
  entity: RenderableEntity,
  mapPoint: ((p: Vec3) => Vec2) | null,
): { points: ReadonlyArray<Vec2>; bboxCorners: boolean } {
  const raw = collectGeometryPoints(entity);
  if (!mapPoint) return { points: raw, bboxCorners: false };
  // iter-4 #2 / iter-7 — the bbox-corner lift applies to any single-point
  // placeholder whose `bounds` is the real shape: a cache-direct stub (bbox
  // centre, original type) OR a real DXF Point with a non-degenerate PDSIZE
  // marker. Trusting the lone geometry point would test the centre alone and
  // miss/over-include the visible bounds that protrude. Everything else with
  // genuine geometry points maps those points unchanged.
  if (
    raw.length > 0 &&
    !isMetadataStubRenderable(entity) &&
    !isNonDegeneratePoint(entity)
  ) {
    return { points: raw.map(mapPoint), bboxCorners: false };
  }
  // Bbox-only entity, spatial stub, OR non-degenerate real Point: map the 4
  // corners so the rotated marker box is tested exactly, and flag them as a
  // closed ring for the crossing test. (A degenerate point — zero-size bounds —
  // fails both detectors and falls through to the single-point map above.)
  return { points: mappedBboxCorners(entity, mapPoint), bboxCorners: true };
}

// ── Per-mode predicates ─────────────────────────────────────────────

/**
 * **Window select**: every vertex (and the full bbox) of the entity
 * must be inside the rectangle. Mirrors the SVG path's bbox-first
 * shortcut (entityBounds.ts:247-252).
 *
 * `mapPoint` (MC4 S2 P2 #C) — when set, the entity's native AABB is in a
 * DIFFERENT space than `selectionWindow` (model vs paper), so the axis-aligned
 * bbox shortcut is invalid; the test runs purely on the mapped representative
 * points instead. When null the bbox shortcut runs as before (byte-equivalent).
 */
function isFullyInsideWindow(
  entity: RenderableEntity,
  selectionWindow: SelectionWindow,
  mapPoint: ((p: Vec3) => Vec2) | null,
): boolean {
  if (!mapPoint) {
    const bounds = entity.bounds;
    if (
      bounds.minX < selectionWindow.left ||
      bounds.maxX > selectionWindow.right ||
      bounds.minY < selectionWindow.top ||
      bounds.maxY > selectionWindow.bottom
    ) {
      // Bbox sticks out; the entity can't be fully inside. Note that the
      // SVG path also does the per-vertex check on top of the bbox check
      // — for line / polyline / circle / arc the bbox alone is sufficient
      // (it's a tight fit), so this short-circuit is safe.
      return false;
    }
  }

  // `testPoints` returns native points (mapPoint null) or mapped points /
  // mapped bbox-corners (mapPoint set). Both are in `selectionWindow`'s space.
  const { points } = testPoints(entity, mapPoint);
  if (points.length === 0) {
    // Bbox-only entity in the IDENTITY path — bbox check already returned true
    // above. (In the mapped path `testPoints` returns the 4 mapped corners, so
    // this branch is identity-only.)
    return true;
  }
  for (const p of points) {
    if (!pointInsideWindow(p, selectionWindow)) return false;
  }
  // iter-5 #2 / iter-6 #2 — for CURVE geometry in the mapped path the bbox
  // shortcut above is skipped (the model AABB is not axis-aligned in paper), so
  // containment rests only on the SAMPLED rim vertices. A circle/arc/ellipse near
  // a window edge can have every coarse sample inside while the true curve bows
  // OUTSIDE the chord between two samples → a left-to-right WINDOW select would
  // wrongly include a not-fully-inside curve.
  //
  // The iter-4 fix tested the curve's mapped AABB CORNERS — but those corners are
  // NOT on the curve (a circle's bbox corner sits at r·√2 from the centre, well
  // beyond the rim at r), so a circle fully inside got WRONGLY REJECTED (false
  // negative). The iter-5 fix sampled the true rim at a FIXED 256 steps — but the
  // chord sagitta is r·(1 − cos(π/256)), which for a 1e6-unit circle bows ~75
  // units past the sampled chord; a window edge between the sampled max and the
  // true rim then wrongly ACCEPTS a not-fully-inside curve (false positive,
  // radius-dependent).
  //
  // iter-6 #2 — test the TRUE TRANSFORMED EXTREMA instead of fixed sampling.
  // The points returned by `curveExtremaPoints` lie exactly on the paper-space
  // outline AND span its full axis extent (paper-min/max X and Y) — so "all
  // extrema inside the axis-aligned window" ≡ "the whole transformed outline is
  // inside", exact regardless of radius. (Crossing mode never reaches here; a
  // slightly-protruding curve that "crosses" is a legitimate crossing hit.)
  if (mapPoint && isCurveGeometry(entity)) {
    for (const p of curveExtremaPoints(entity, mapPoint)) {
      if (!pointInsideWindow(p, selectionWindow)) return false;
    }
  }
  return true;
}

/**
 * iter-4 #5 / iter-5 #2 / iter-6 #2 — curve kinds whose true outline bows OUTSIDE
 * the chords of a coarse sampled polyline (so coarse-sample containment
 * under-rejects). For these, mapped WINDOW select additionally requires every
 * TRUE TRANSFORMED EXTREMUM inside (`curveExtremaPoints`), not just the off-curve
 * bbox corners (iter-4) or a fixed-density rim sampling (iter-5).
 */
function isCurveGeometry(entity: RenderableEntity): boolean {
  const k = entity.geometry.kind;
  return k === "circle" || k === "arc" || k === "ellipse";
}

/**
 * iter-6 #2 — the curve's TRUE paper-space axis extrema (and endpoints for an
 * arc), computed ANALYTICALLY rather than by fixed sampling.
 *
 * `mapPoint` (= `space.fromSpace`, modelToPaper) is a 2D AFFINE map (the VP
 * transform is rotation + uniform scale + translation — isotropic, no shear). For
 * ANY affine map the image of a model point `center + (u, v)` is
 *
 *     mapPoint(center) + u·Ex + v·Ey
 *
 * where `Ex = mapPoint(center + (1,0)) − mapPoint(center)` and
 * `Ey = mapPoint(center + (0,1)) − mapPoint(center)` are the mapped basis
 * vectors. We recover Ex/Ey by sampling the map (two extra calls), so the math
 * is exact even though `mapPoint` is opaque.
 *
 *   - circle (param α): paper point = C + r·(cosα·Ex + sinα·Ey). The paper-x
 *     extent is extremised at αx = atan2(Ey.x, Ex.x) (and αx+π); paper-y at
 *     αy = atan2(Ey.y, Ex.y) (and αy+π). Those 4 on-curve points give the exact
 *     paper-space min/max X and Y — radius-independent, no sampling error.
 *   - arc: the same 4 axis-extremum params, but only those inside the angular
 *     sweep, PLUS the two endpoints (always on the outline). Exact.
 *   - ellipse (param t): paper point = C + cos(t)·P + sin(t)·Q where P, Q are the
 *     mapped images of the ellipse's conjugate semi-axes (major M and
 *     minor N = ratio·M⊥). Axis extrema at tx = atan2(Q.x, P.x) / ty =
 *     atan2(Q.y, P.y) (and +π), filtered to the sweep, plus endpoints for a
 *     partial ellipse. Exact for full and partial ellipses alike.
 *
 * Requiring every returned point inside the axis-aligned window ≡ the whole
 * transformed outline's paper AABB ⊆ window ≡ (for a single convex/curve arc)
 * the outline fully inside. Returns `[]` for non-curve kinds (the caller gates on
 * `isCurveGeometry` first).
 */
function curveExtremaPoints(
  entity: RenderableEntity,
  mapPoint: (p: Vec3) => Vec2,
): Vec2[] {
  const geom = entity.geometry;
  switch (geom.kind) {
    case "circle":
      return circleExtrema(geom.center, geom.radius, mapPoint);
    case "arc":
      return arcExtrema(
        geom.center,
        geom.radius,
        geom.start,
        geom.end,
        mapPoint,
      );
    case "ellipse":
      return ellipseExtrema(
        geom.center,
        geom.majorAxis,
        geom.ratio,
        geom.start,
        geom.end,
        mapPoint,
        geom.extrusion,
      );
    default:
      return [];
  }
}

/**
 * Recover the mapped basis vectors `Ex`, `Ey` and the mapped center `C` of the
 * affine `mapPoint` anchored at the model point `center`. `mapPoint(center +
 * (u,v)) = C + u·Ex + v·Ey` exactly for any affine map.
 */
function mappedBasis(
  center: Vec3,
  mapPoint: (p: Vec3) => Vec2,
): { C: Vec2; Ex: Vec2; Ey: Vec2 } {
  const z = center.z;
  const C = mapPoint(center);
  const px = mapPoint({ x: center.x + 1, y: center.y, z });
  const py = mapPoint({ x: center.x, y: center.y + 1, z });
  return {
    C,
    Ex: { x: px.x - C.x, y: px.y - C.y },
    Ey: { x: py.x - C.x, y: py.y - C.y },
  };
}

/** Paper point on the circle at model angle α: C + r·(cosα·Ex + sinα·Ey). */
function circlePointAt(
  C: Vec2,
  Ex: Vec2,
  Ey: Vec2,
  r: number,
  angle: number,
): Vec2 {
  const ca = Math.cos(angle);
  const sa = Math.sin(angle);
  return {
    x: C.x + r * (ca * Ex.x + sa * Ey.x),
    y: C.y + r * (ca * Ex.y + sa * Ey.y),
  };
}

/** The 4 model angles that extremise paper-x / paper-y of a circle/arc. */
function circleAxisExtremaAngles(Ex: Vec2, Ey: Vec2): number[] {
  const ax = Math.atan2(Ey.x, Ex.x); // extremises paper-x
  const ay = Math.atan2(Ey.y, Ex.y); // extremises paper-y
  return [ax, ax + Math.PI, ay, ay + Math.PI];
}

/** Full circle → its 4 true paper-space axis-extremum points. */
function circleExtrema(
  center: Vec3,
  radius: number,
  mapPoint: (p: Vec3) => Vec2,
): Vec2[] {
  const { C, Ex, Ey } = mappedBasis(center, mapPoint);
  return circleAxisExtremaAngles(Ex, Ey).map((a) =>
    circlePointAt(C, Ex, Ey, radius, a),
  );
}

/**
 * Arc → endpoints (always) + each paper-axis-extremum angle that falls within
 * the normalised sweep. Mirrors `sampleArcOutline`'s sweep normalisation so the
 * angular domain matches the coarse render-path sampling.
 */
function arcExtrema(
  center: Vec3,
  radius: number,
  start: number,
  end: number,
  mapPoint: (p: Vec3) => Vec2,
): Vec2[] {
  const { C, Ex, Ey } = mappedBasis(center, mapPoint);
  let sweep = end - start;
  if (sweep <= 0) sweep += Math.PI * 2;
  const out: Vec2[] = [
    circlePointAt(C, Ex, Ey, radius, start),
    circlePointAt(C, Ex, Ey, radius, start + sweep),
  ];
  for (const a of circleAxisExtremaAngles(Ex, Ey)) {
    if (angleInSweep(a, start, sweep)) {
      out.push(circlePointAt(C, Ex, Ey, radius, a));
    }
  }
  return out;
}

/**
 * Ellipse → analytic axis extrema in paper space. The ellipse at param t is
 * `center + cos(t)·M + sin(t)·N` with M = majorAxis and N = ratio·(M rotated
 * +90°). Mapping each conjugate semi-axis through the linear part gives P, Q, and
 * the paper point is `C + cos(t)·P + sin(t)·Q`. Axis extrema at
 * tx = atan2(Q.x, P.x) / ty = atan2(Q.y, P.y) (and +π), filtered to the sweep,
 * plus endpoints for a partial ellipse.
 */
function ellipseExtrema(
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  start: number,
  end: number,
  mapPoint: (p: Vec3) => Vec2,
  extrusion?: Vec3,
): Vec2[] {
  const { C, Ex, Ey } = mappedBasis(center, mapPoint);
  // Conjugate semi-axes in MODEL space: M (major) and N (minor). SL-15 round-8
  // @codex F4 — the minor rides the TRUE 3D axis (`extrusion × major`, ezdxf
  // `minor_axis`) projected to XY, NOT a flat XY rot90, so a TILTED ellipse's
  // paper extrema match its rendered silhouette. Planar ⇒ byte-identical to the
  // old `(−My·ratio, Mx·ratio)`.
  const Mx = majorAxis.x;
  const My = majorAxis.y;
  const majorMag3D = Math.hypot(majorAxis.x, majorAxis.y, majorAxis.z);
  const minorUnit = ellipseMinorUnit(majorAxis, extrusion);
  const Nx = minorUnit.x * majorMag3D * ratio;
  const Ny = minorUnit.y * majorMag3D * ratio;
  // Their images under the linear part (u·Ex + v·Ey).
  const P = { x: Mx * Ex.x + My * Ey.x, y: Mx * Ex.y + My * Ey.y };
  const Q = { x: Nx * Ex.x + Ny * Ey.x, y: Nx * Ex.y + Ny * Ey.y };
  const pointAt = (t: number): Vec2 => {
    const ct = Math.cos(t);
    const st = Math.sin(t);
    return { x: C.x + ct * P.x + st * Q.x, y: C.y + ct * P.y + st * Q.y };
  };
  let sweep = end - start;
  if (sweep <= 0) sweep += Math.PI * 2;
  const isFullTurn = Math.abs(sweep - Math.PI * 2) < 1e-9;
  const tx = Math.atan2(Q.x, P.x);
  const ty = Math.atan2(Q.y, P.y);
  const extremaParams = [tx, tx + Math.PI, ty, ty + Math.PI];
  const out: Vec2[] = [];
  if (!isFullTurn) {
    // Endpoints are on the outline for a partial ellipse arc.
    out.push(pointAt(start), pointAt(start + sweep));
  }
  for (const t of extremaParams) {
    if (isFullTurn || angleInSweep(t, start, sweep)) out.push(pointAt(t));
  }
  return out;
}

/** True iff `angle` lies within the sweep `[start, start + sweep]` (mod 2π). */
function angleInSweep(angle: number, start: number, sweep: number): boolean {
  const TWO_PI = Math.PI * 2;
  let delta = (angle - start) % TWO_PI;
  if (delta < 0) delta += TWO_PI;
  // Tolerate float drift at the endpoints (already emitted explicitly anyway).
  return delta <= sweep + 1e-9;
}

// ── Coarse render-path curve sampling (collectGeometryPoints) ────────────────
//
// These coarse samplers (16 circle samples / ~20°-step arc / 32 ellipse) feed
// `collectGeometryPoints` — the render-path vertex list used for the AABB
// prefilter and the crossing/segment tests. They are intentionally kept
// byte-equivalent to the legacy density. The EXACT mapped-window curve
// containment no longer samples: it uses the analytic `curveExtremaPoints` above
// (iter-6 #2), so the curve sagitta error of fixed sampling is eliminated.

/** Full circle as `samples` points (CLOSED — no duplicate end vertex). */
function sampleCircleOutline(
  center: Vec3,
  radius: number,
  samples: number,
): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < samples; i++) {
    const angle = (i / samples) * Math.PI * 2;
    out.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
      z: center.z,
    });
  }
  return out;
}

/** Arc from `start`→`end` (sweep normalised positive) as `samples`+1 points
 * (OPEN — both endpoints included). */
function sampleArcOutline(
  center: Vec3,
  radius: number,
  start: number,
  end: number,
  samples: number,
): Vec3[] {
  let sweep = end - start;
  if (sweep <= 0) sweep += Math.PI * 2;
  const out: Vec3[] = [];
  for (let i = 0; i <= samples; i++) {
    const angle = start + (sweep * i) / samples;
    out.push({
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
      z: center.z,
    });
  }
  return out;
}

/**
 * Ellipse arc `start`→`end` as `samples`+1 points. SL-15 round-8 @codex F4 —
 * delegates to the ONE-TRUTH 3D render sampler (`tessellateEllipse`) so a TILTED
 * ellipse's window/crossing outline matches its rendered silhouette (the paper
 * `mapPoint` reads XY, and the 3D samples' XY IS the visible outline). A planar
 * ellipse yields byte-identical points to the prior XY-only formula.
 */
function sampleEllipseOutline(
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  start: number,
  end: number,
  samples: number,
  extrusion?: Vec3,
): Vec3[] {
  return tessellateEllipse(
    center,
    majorAxis,
    ratio,
    start,
    end,
    samples,
    extrusion,
  );
}

// ── Mapped crossing helpers (iter-8) ─────────────────────────────────────────

/**
 * iter-8 #1 — ADAPTIVE mapped-curve sampling for the CROSSING test.
 *
 * The coarse `collectGeometryPoints` density (16 circle / ~20° arc / 32 ellipse)
 * feeds the AABB prefilter and the legacy crossing test, but its chords can bow
 * far past the true rim. WINDOW mode got the analytic `curveExtremaPoints` fix
 * (iter-6), yet crossing never did: a SMALL crossing window touching the curve
 * BETWEEN two coarse samples has no sampled vertex inside AND no chord
 * intersecting the window, so a visibly-crossing curve is MISSED.
 *
 * The robust fix mirrors what AutoCAD's pixel-accurate crossing does: refine the
 * sampling until the chord SAGITTA — the max distance from a chord to the true
 * rim, `r_paper·(1 − cos(π/N))` — is well below the window's smaller dimension.
 * Then no crossing window can slip between two samples without either enclosing a
 * sample or crossing a chord. `r_paper` is the curve's PAPER-space radius (the
 * mapped basis already encodes the VP scale, so this is radius-independent of
 * the model units). `k = 0.25` keeps the sagitta at ≤¼ of the smaller window
 * edge — comfortably below the gap a crossing window could exploit.
 *
 * Returns the adaptively-sampled MAPPED rim points plus whether they form a
 * CLOSED ring (full circle / full ellipse) vs an OPEN arc (`sampleArcOutline` /
 * a partial ellipse already include both endpoints). `null` for non-curve kinds
 * (caller falls back to the coarse `testPoints` path).
 */
function mappedCurveCrossingSamples(
  entity: RenderableEntity,
  mapPoint: (p: Vec3) => Vec2,
  selectionWindow: SelectionWindow,
): { points: Vec2[]; closed: boolean } | null {
  const geom = entity.geometry;
  if (geom.kind !== "circle" && geom.kind !== "arc" && geom.kind !== "ellipse") {
    return null;
  }
  const minWindow = Math.min(
    selectionWindow.right - selectionWindow.left,
    selectionWindow.bottom - selectionWindow.top,
  );
  let model: Vec3[];
  let closed: boolean;
  switch (geom.kind) {
    case "circle": {
      const rPaper = mappedCurveRadius(geom.center, geom.radius, mapPoint);
      const n = adaptiveSampleCount(rPaper, minWindow);
      model = sampleCircleOutline(geom.center, geom.radius, n);
      closed = true;
      break;
    }
    case "arc": {
      const rPaper = mappedCurveRadius(geom.center, geom.radius, mapPoint);
      const n = adaptiveSampleCount(rPaper, minWindow);
      model = sampleArcOutline(geom.center, geom.radius, geom.start, geom.end, n);
      closed = false; // sampleArcOutline already includes both endpoints.
      break;
    }
    default: {
      // ellipse — paper extent is the larger mapped conjugate-axis magnitude.
      const rPaper = mappedEllipseRadius(
        geom.center,
        geom.majorAxis,
        geom.ratio,
        mapPoint,
        geom.extrusion,
      );
      const n = adaptiveSampleCount(rPaper, minWindow);
      model = sampleEllipseOutline(
        geom.center,
        geom.majorAxis,
        geom.ratio,
        geom.start,
        geom.end,
        n,
        geom.extrusion,
      );
      // Full turn → closed ring; partial ellipse arc → open (endpoints sampled).
      let sweep = geom.end - geom.start;
      if (sweep <= 0) sweep += Math.PI * 2;
      closed = Math.abs(sweep - Math.PI * 2) < 1e-9;
      break;
    }
  }
  return { points: model.map(mapPoint), closed };
}

/** Paper-space radius of a circle/arc: model radius × the mapped uniform scale. */
function mappedCurveRadius(
  center: Vec3,
  radius: number,
  mapPoint: (p: Vec3) => Vec2,
): number {
  const { Ex, Ey } = mappedBasis(center, mapPoint);
  // Isotropic VP map → |Ex| ≈ |Ey|; take the larger to never under-sample.
  const scale = Math.max(Math.hypot(Ex.x, Ex.y), Math.hypot(Ey.x, Ey.y));
  return radius * scale;
}

/** Paper-space half-extent of an ellipse: the larger mapped conjugate semi-axis. */
function mappedEllipseRadius(
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  mapPoint: (p: Vec3) => Vec2,
  extrusion?: Vec3,
): number {
  const { Ex, Ey } = mappedBasis(center, mapPoint);
  // SL-15 round-10 @codex F3 — size the paper half-extent (and hence the crossing
  // sample COUNT) from the SAME 3D semi-axes `tessellateEllipse` traces, NOT the
  // XY-only planar minor. The sampler uses rx = |majorAxis| (FULL 3D length) and the
  // 3D minor unit `ellipseMinorUnit(major, extrusion)` scaled by ry = rx·ratio. For a
  // TILTED ellipse whose major is (near) parallel to z, the XY-planar minor
  // `(−My·ratio, Mx·ratio)` foreshortens to ~0 while the TRUE 3D minor projects to a
  // large paper extent → the old formula under-estimated rPaper → too few samples →
  // crossing-select missed part of the rim. Project the true 3D semi-axes' XY through
  // the mapped basis. A PLANAR ellipse (default (0,0,1) normal, major in XY) yields
  // `ay·ry = (−My·ratio, Mx·ratio, 0)` → byte-identical to the prior formula.
  const rx = Math.hypot(majorAxis.x, majorAxis.y, majorAxis.z);
  const ry = rx * ratio;
  const ay = ellipseMinorUnit(majorAxis, extrusion);
  // Major semi-axis paper image: its XY IS `ax·rx = (majorAxis.x, majorAxis.y)`.
  const Mx = majorAxis.x;
  const My = majorAxis.y;
  // Minor semi-axis paper image: the 3D minor `ay·ry` projected (XY components only,
  // the paper map is XY-affine — z is dropped by the top-down projection).
  const Nx = ay.x * ry;
  const Ny = ay.y * ry;
  // Images of the conjugate semi-axes under the linear part (u·Ex + v·Ey).
  const P = { x: Mx * Ex.x + My * Ey.x, y: Mx * Ex.y + My * Ey.y };
  const Q = { x: Nx * Ex.x + Ny * Ey.x, y: Nx * Ex.y + Ny * Ey.y };
  return Math.max(Math.hypot(P.x, P.y), Math.hypot(Q.x, Q.y));
}

/** Lower bound on the adaptive curve-crossing sample count for window slack. */
const MIN_CURVE_CROSSING_SAMPLES = 16;
/** Upper bound — guards a degenerate (rPaper ≫ window) blow-up. */
const MAX_CURVE_CROSSING_SAMPLES = 4096;
/** Sagitta-to-window slack factor: chord bow ≤ ¼ of the smaller window edge. */
const CURVE_CROSSING_SAGITTA_FACTOR = 0.25;

/**
 * Smallest `N` for which the chord sagitta `r·(1 − cos(π/N))` of an `N`-gon on a
 * radius-`r` circle stays below `minWindow · k`. Solving the small-angle form
 * `r·π²/(2N²) ≤ minWindow·k` for N gives `N ≥ π·√(r / (2·minWindow·k))`; we use
 * that closed form, then clamp to the [MIN, MAX] band. A non-positive radius or
 * window (degenerate) collapses to the floor.
 */
function adaptiveSampleCount(rPaper: number, minWindow: number): number {
  if (
    !(rPaper > 0) ||
    !(minWindow > 0) ||
    !Number.isFinite(rPaper) ||
    !Number.isFinite(minWindow)
  ) {
    return MIN_CURVE_CROSSING_SAMPLES;
  }
  const slack = minWindow * CURVE_CROSSING_SAGITTA_FACTOR;
  const n = Math.ceil(Math.PI * Math.sqrt(rPaper / (2 * slack)));
  return Math.min(
    MAX_CURVE_CROSSING_SAMPLES,
    Math.max(MIN_CURVE_CROSSING_SAMPLES, n),
  );
}

/** True iff `p` lies inside the simple polygon `poly` (winding-agnostic). */
function pointInPolygon(p: Vec2, poly: ReadonlyArray<Vec2>): boolean {
  // Even-odd ray-cast. Works for any simple polygon; the mapped bbox is a convex
  // quad. The inverse-containment caller only asks about window corners that are
  // strictly enclosed by the bbox, so exact on-edge classification is moot — a
  // window touching the bbox edge is already caught by the segment-crossing test
  // above. (A precisely-on-edge corner may classify either way here.)
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const intersects =
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * **Crossing select**: entity is selected if (any vertex inside) OR
 * (any segment intersects rectangle border) OR (bbox overlaps and
 * entity is bbox-only). Mirrors entityBounds.ts:255-307.
 *
 * `mapPoint` (MC4 S2 P2 #C) — see {@link isFullyInsideWindow}. When set the
 * bbox fast-paths are skipped (model bbox is not axis-aligned in paper) and the
 * crossing test runs on mapped points + mapped segments against the paper rect.
 */
function crossesWindow(
  entity: RenderableEntity,
  selectionWindow: SelectionWindow,
  mapPoint: ((p: Vec3) => Vec2) | null,
): boolean {
  if (!mapPoint) {
    const bounds = entity.bounds;
    // Fast path: entity bbox fully inside → guaranteed crossing select hit.
    if (
      bounds.minX >= selectionWindow.left &&
      bounds.maxX <= selectionWindow.right &&
      bounds.minY >= selectionWindow.top &&
      bounds.maxY <= selectionWindow.bottom
    ) {
      return true;
    }
  }

  // iter-8 #1 — in the mapped path a CURVE is tested with ADAPTIVE rim sampling
  // (sagitta ≪ window) instead of the coarse `collectGeometryPoints` density, so
  // a small crossing window touching the rim BETWEEN two coarse samples can no
  // longer slip through. The identity path keeps the coarse `testPoints` density
  // (byte-equivalent); window mode is unaffected (it never reaches here).
  const mappedCurve = mapPoint
    ? mappedCurveCrossingSamples(entity, mapPoint, selectionWindow)
    : null;
  let points: ReadonlyArray<Vec2>;
  let bboxCorners: boolean;
  let segments: Array<{ start: Vec2; end: Vec2 }>;
  if (mappedCurve) {
    points = mappedCurve.points;
    bboxCorners = false;
    segments = buildClosablePointSegments(mappedCurve.points, mappedCurve.closed);
  } else {
    const tp = testPoints(entity, mapPoint);
    points = tp.points;
    bboxCorners = tp.bboxCorners;
    // Build segments from the (possibly mapped) points, mirroring the geometry's
    // open/closed topology. In the identity path this equals
    // `collectGeometrySegments(entity)`; in the mapped path each endpoint is the
    // model→paper image of the same vertex. `bboxCorners` (iter-4 #3) forces the
    // closing edge when the points are the entity's 4 mapped bbox corners.
    segments = buildTestSegments(entity, points, bboxCorners);
  }

  // Any vertex inside?
  for (const p of points) {
    if (pointInsideWindow(p, selectionWindow)) return true;
  }

  // Any segment crosses the rectangle border?
  for (const seg of segments) {
    if (segmentIntersectsWindow(seg.start, seg.end, selectionWindow)) {
      return true;
    }
  }

  // iter-8 #2 — INVERSE containment for the mapped bbox-corner path. A
  // right-to-left crossing rubber band that lies ENTIRELY INSIDE a large mapped
  // bbox (a block / text / dimension / multileader / stub / non-degenerate point
  // whose visible rectangle surrounds the small window) has no bbox corner inside
  // the window AND no bbox edge crossing the window border — yet the legacy
  // bbox-OVERLAP contract (window inside the entity) selects it. The identity
  // path catches this via the AABB-overlap fallback below; the mapped path emits
  // the 4 corners as `points`, so it must test "any window corner inside the
  // mapped bbox polygon" explicitly. (Window mode is fully-inside semantics and
  // never reaches here, so this is crossing-only.)
  if (bboxCorners && points.length >= 3) {
    for (const corner of windowCorners(selectionWindow)) {
      if (pointInPolygon(corner, points)) return true;
    }
  }

  // Bbox-only entities (no points/segments collected) — fall back to a
  // pure AABB overlap test, mirroring entityBounds.ts:301-306. Only the
  // identity path reaches here with zero points (the mapped path emits the
  // 4 bbox corners as points, handled by the vertex/segment tests above).
  if (segments.length === 0 && points.length === 0) {
    const bounds = entity.bounds;
    return !(
      bounds.maxX < selectionWindow.left ||
      bounds.minX > selectionWindow.right ||
      bounds.maxY < selectionWindow.top ||
      bounds.minY > selectionWindow.bottom
    );
  }
  return false;
}

/** The 4 corners of a selection window (CCW), for the inverse-containment test. */
function windowCorners(w: SelectionWindow): Vec2[] {
  return [
    { x: w.left, y: w.top },
    { x: w.right, y: w.top },
    { x: w.right, y: w.bottom },
    { x: w.left, y: w.bottom },
  ];
}

/**
 * iter-8 #1 — segments for an adaptively-sampled mapped curve. Mirrors
 * `buildTestSegments`' ring logic but takes the closed/open flag directly (the
 * sample list already encodes the curve's topology: a full circle/ellipse is a
 * closed ring; an arc / partial ellipse already includes both endpoints OPEN).
 */
function buildClosablePointSegments(
  points: ReadonlyArray<Vec2>,
  closed: boolean,
): Array<{ start: Vec2; end: Vec2 }> {
  if (points.length < 2) return [];
  const segs: Array<{ start: Vec2; end: Vec2 }> = [];
  for (let i = 0; i < points.length - 1; i++) {
    segs.push({ start: points[i]!, end: points[i + 1]! });
  }
  if (closed) {
    segs.push({ start: points[points.length - 1]!, end: points[0]! });
  }
  return segs;
}

/**
 * Build the crossing-test segment list from a point list, honouring the
 * entity's open/closed topology (mirror of `collectGeometrySegments`' ring
 * logic). Extracted so the IDENTITY path and the mapped (paper-space) path
 * share one segment-construction rule — only the point coords differ.
 *
 * `bboxCorners` (iter-4 #3) — when the supplied points are the entity's 4 mapped
 * BBOX CORNERS (a bbox-only kind like text / block / dimension / multileader, OR
 * a cache-direct spatial stub), the ring MUST be closed regardless of the
 * geometry kind. Those kinds are not in the `closeRing` set below, so without
 * this flag a crossing drag that touches ONLY the missing 4th edge (start→last
 * corner) would see no tested segment intersection and miss the visible bbox.
 */
function buildTestSegments(
  entity: RenderableEntity,
  points: ReadonlyArray<Vec2>,
  bboxCorners = false,
): Array<{ start: Vec2; end: Vec2 }> {
  if (points.length < 2) return [];
  const isPolylineClosed =
    entity.geometry.kind === "polyline" && entity.geometry.closed;
  const isCircleish =
    entity.geometry.kind === "circle" ||
    entity.geometry.kind === "hatch" ||
    entity.geometry.kind === "wipeout" ||
    entity.geometry.kind === "viewport";
  const closeRing = bboxCorners || isCircleish || isPolylineClosed;

  const segs: Array<{ start: Vec2; end: Vec2 }> = [];
  for (let i = 0; i < points.length - 1; i++) {
    segs.push({ start: points[i]!, end: points[i + 1]! });
  }
  if (closeRing && points.length >= 2) {
    segs.push({ start: points[points.length - 1]!, end: points[0]! });
  }
  return segs;
}

// ── Geometry → points / segments extraction ────────────────────────

/**
 * Collect the entity's representative vertices for a window-select
 * test. Mirrors `buildEntitySelectionGeometry` (entityBounds.ts:309).
 *
 * Returns an empty array for bbox-only entity kinds; callers fall back
 * to bbox math in that case.
 */
function collectGeometryPoints(entity: RenderableEntity): readonly Vec3[] {
  const geom = entity.geometry;
  switch (geom.kind) {
    case "line":
      return [geom.a, geom.b];
    case "circle":
      // 16 samples — same density as entityBounds.ts:325-327.
      return sampleCircleOutline(geom.center, geom.radius, 16);
    case "arc": {
      // entityBounds.ts uses `Math.max(6, Math.ceil(span / 20))` where
      // span is in degrees → 1 step every ~20° of sweep. `sampleArcOutline`
      // normalises the sweep the same way, so compute the step count from the
      // SAME normalised sweep to stay byte-equivalent to the legacy coarse path.
      let sweep = geom.end - geom.start;
      if (sweep <= 0) sweep += Math.PI * 2;
      const sweepDeg = (sweep * 180) / Math.PI;
      const steps = Math.max(6, Math.ceil(sweepDeg / 20));
      return sampleArcOutline(
        geom.center,
        geom.radius,
        geom.start,
        geom.end,
        steps,
      );
    }
    case "polyline":
      // A BULGED span is the circular ARC it encodes, drawn in the entity's own
      // OCS plane (`geom.extrusion`) — `batchedLayer.ts` strokes exactly these
      // samples and `raycast/distance.ts` measures the pick against them. The
      // raw vertices connected as straight chords are a curve NOTHING draws, so
      // a crossing rectangle touching only the BOW of a span saw no vertex
      // inside and no chord crossed, and a visibly-crossed entity was missed —
      // even though the render AABB (which unions the real arc boxes) had
      // already admitted it as a candidate (@codex PR #785 round 7).
      //
      // `polylineCentrelineSamples` returns the INPUT ARRAY UNTOUCHED for every
      // bulge-free polyline, so a straight run keeps the byte-identical point
      // list — the same reference, not a copy — it always had.
      return polylineCentrelineSamples(
        geom.points,
        geom.closed,
        geom.bulges,
        geom.extrusion,
      );
    case "mesh-wire":
      // Order-independent point hull. `entityMatchesWindow` tests a mesh
      // per-RUN and returns before reaching here whenever any run is testable,
      // so this only ever backs the degenerate no-testable-run fall-through
      // (and keeps the exhaustive switch honest).
      return geom.runs.flatMap((run) => run.points);
    case "ellipse":
      // Coarse 32-sample fallback so window-select picks up clicks on
      // the ellipse rim. Matches the renderer's PR-B tessellation
      // density rather than the SVG path (which is bbox-only for
      // ellipse) — this is a deliberate parity drift documented in
      // hit-test-drift.md §3. `sampleEllipseOutline` normalises a
      // non-positive sweep to a full turn (the legacy `sweep === 0` guard).
      return sampleEllipseOutline(
        geom.center,
        geom.majorAxis,
        geom.ratio,
        geom.start,
        geom.end,
        32,
        geom.extrusion,
      );
    case "spline":
      // Control-polyline approximation, matching distance.ts.
      return geom.controlPoints;
    case "hatch": {
      const loop =
        geom.loops.find((l) => l.kind === "exterior") ?? geom.loops[0];
      return loop ? loop.vertices : [];
    }
    case "wipeout":
      return geom.corners;
    case "viewport":
      return geom.rect;
    case "point":
      return [geom.position];
    case "text":
    case "mtext":
    case "dimension":
    case "multileader":
    case "array-rect":
      return [];
    case "block-insert":
      // A wrapper WITH children never reaches here (`entityMatchesWindow`
      // dispatches to the children first); the legacy instanced stub is
      // bbox-only.
      return [];
    default: {
      const _exhaustive: never = geom;
      void _exhaustive;
      return [];
    }
  }
}

// ── Geometric primitives ──────────────────────────────────────────

function pointInsideWindow(p: Vec2, w: SelectionWindow): boolean {
  return p.x >= w.left && p.x <= w.right && p.y >= w.top && p.y <= w.bottom;
}

function segmentIntersectsWindow(
  start: Vec2,
  end: Vec2,
  w: SelectionWindow,
): boolean {
  if (pointInsideWindow(start, w) || pointInsideWindow(end, w)) return true;
  const corners: Vec2[] = [
    { x: w.left, y: w.top },
    { x: w.right, y: w.top },
    { x: w.right, y: w.bottom },
    { x: w.left, y: w.bottom },
  ];
  for (let i = 0; i < 4; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % 4]!;
    if (segmentsIntersect(start, end, a, b)) return true;
  }
  return false;
}

function segmentsIntersect(
  aStart: Vec2,
  aEnd: Vec2,
  bStart: Vec2,
  bEnd: Vec2,
): boolean {
  const denominator =
    (aEnd.x - aStart.x) * (bEnd.y - bStart.y) -
    (aEnd.y - aStart.y) * (bEnd.x - bStart.x);
  if (Math.abs(denominator) < 0.0001) return false;
  const ua =
    ((bEnd.x - bStart.x) * (aStart.y - bStart.y) -
      (bEnd.y - bStart.y) * (aStart.x - bStart.x)) /
    denominator;
  const ub =
    ((aEnd.x - aStart.x) * (aStart.y - bStart.y) -
      (aEnd.y - aStart.y) * (aStart.x - bStart.x)) /
    denominator;
  return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}
